import {
  AccountSummary,
  AppSession,
  FreePlan,
} from "../features/accounts/account";
import { parseDate } from "../features/data/time";
import { logError, LoginTime } from "./logging";

// `Intercom` and `intercomSettings` are globals declared by @types/intercom-web
// (declare var Intercom + declare var intercomSettings + Window augmentation).
// Do NOT add `declare global` here — it will collide. See node_modules/@types/intercom-web/index.d.ts.

const intercomAppId = "qb9h3t4f";

let userIntercomSettings = { app_id: intercomAppId, account_id: undefined };

// I47 - the counts Intercom last received for this user. bootIntercom sends them once per page
// load, but a new user imports, builds and publishes without reloading, so First Catalog V2's
// day-4 split (`products`) and exit (`catalogs_published`) read values frozen at boot.
// null = no identified user, so nothing is sent.
type IntercomCounts = {
  products: number;
  catalogs: number;
  catalogs_published: number;
};

let sentCounts: IntercomCounts | null = null;
let countsCompany: { id: number; name: string } | undefined;
let isAdminSession = false;

const countsOf = (summary: AccountSummary): IntercomCounts => ({
  products: summary.itemsCount ?? 0,
  catalogs: summary.catalogsCount ?? 0,
  catalogs_published: summary.publishedCatalogs ?? 0,
});

/** Sends fresh counts to Intercom when one moved; the 0 -> 1 publish also fires `catalog-published`. */
export const syncIntercomCounts = (summary?: AccountSummary) => {
  if (!sentCounts || !summary) return;
  const prev = sentCounts;
  const next = countsOf(summary);
  if (
    next.products === prev.products &&
    next.catalogs === prev.catalogs &&
    next.catalogs_published === prev.catalogs_published
  ) {
    return;
  }
  sentCounts = next;
  try {
    // Same attributes boot writes on both the user and the company.
    Intercom("update", {
      ...next,
      company: { ...countsCompany, ...next },
    });
    // An account that unpublished everything and publishes again fires it again; it is the
    // first-publish moment for every practical case, and Intercom keeps the event count.
    // Not on an impersonated session: that publish is ours, not the user's.
    if (
      prev.catalogs_published === 0 &&
      next.catalogs_published > 0 &&
      !isAdminSession
    ) {
      Intercom("trackEvent", "catalog-published");
    }
  } catch (e) {
    logError(e);
  }
};

/**
 * S99 H - the onboarding `audience` answer, sent at the tap. Boot wrote the profile attributes
 * before the question was asked, so without this a signup stays unanswered in Intercom until the
 * next page load.
 */
export const sendIntercomAudience = (audience: string) => {
  if (!sentCounts) return;
  try {
    Intercom("update", {
      audience,
      company: { ...countsCompany, audience },
    });
  } catch (e) {
    logError(e);
  }
};

/**
 * S99 H - a plan wall, as an Intercom event a message can trigger on. The count attributes cannot
 * stand in for it: the import walls show before anything is saved, on an account with 0 products.
 * Not on an impersonated session: that wall is ours, not the user's.
 */
export const trackIntercomWall = (
  type: string,
  entryPoint: string | undefined,
  plan: string | undefined,
) => {
  if (!sentCounts || isAdminSession) return;
  try {
    Intercom("trackEvent", "wall-shown", {
      type,
      entry_point: entryPoint,
      plan,
    });
  } catch (e) {
    logError(e);
  }
};

export const bootIntercom = (session?: AppSession, loginTime?: LoginTime) => {
  // Identification is DECOUPLED from launcher visibility (the Fin-cost gate), and
  // is UNCONDITIONAL for a signed-in account: if we have a session, Intercom gets
  // the identity. Contacts are not billed (Intercom bills Fin resolutions +
  // outbound email — verified against the May 2026 invoice, see a63e4d17), so
  // withholding a name saves nothing and costs support the ability to see who is
  // writing in.
  //
  // This replaces an intent heuristic (paid OR a product OR a catalog OR a return
  // session) that ran ONCE per session load and could not be re-evaluated. Hornet
  // Outdoors #327433 failed all four at page load, then created 10 products and a
  // catalog without ever reloading, and reached Fin as the anonymous lead "Pink Bus
  // from Pengilly" while signed in — so the escalation was paid for and the identity
  // lost anyway. Fin exposure is gated below on `support`, NOT here.
  const shouldIdentify = !!session?.account;

  try {
    const accountInfo = shouldIdentify
      ? {
          namespace: session.account.namespace,
          category: session.account.planCode === FreePlan ? FreePlan : "paid",
          plan: session.account.planCode,
          plan_desc: session.account.priceDescription,
          plan_gateway: session.account.gateway,
          price: session.account.monthlyPrice,
          account_status: session.account.subscriptionStatus,
          support: session.account.support,
          shop: session.account.shop,
          showrooms: session.account.summary.showrooms ?? 0,
          showroom_privacy: session.account.summary.showroomPrivacy
            ? "closed"
            : "open",
          sessions:
            session.account.summary.sessions > 0
              ? session.account.summary.sessions
              : undefined,
          products: session.account.summary.itemsCount ?? 0,
          categories: session.account.summary.categoriesCount ?? 0,
          catalogs: session.account.summary.catalogsCount ?? 0,
          users: session.account.summary.usersCount ?? 0,
          catalogs_published: session.account.summary.publishedCatalogs ?? 0,
          storage: session.account.summary.imageSizeMb ?? 0,
          ...session?.account?.summary.metaProfile.reduce((acc, cur) => {
            acc[cur.item1] = cur.item2;
            return acc;
          }, {}),
          // S71 — one click from an Intercom conversation into this account's support console.
          // Both halves matter: the id opens THIS account, and ?email= drives the
          // related-accounts strip, because one person can own several accounts and the id
          // here is only whichever one they were signed into when this was last written.
          // Intercom renders a URL-valued custom attribute as a link in the sidebar.
          support_url: `${window.location.origin}/support/${session.account.id}?email=${encodeURIComponent(
            session.user.email,
          )}`,
        }
      : {};

    userIntercomSettings = {
      ...userIntercomSettings,
      ...(session?.account
        ? {
            name: session.user.name,
            email: session.user.email,
            user_hash: session.user.hmac,
            created_at: parseDate(session.user.created, true).getTime() / 1000,
            user_id: "" + session?.user?.id,
            account_id: session.account.id,
            app_version: "v2",
            ...accountInfo,
            role: session.user.role,
            business_name: session.account.businessName,
            session_mode: session.provider === "Admin" ? "admin" : "",
            signup_source: session.account.signUpSource,
            url_ref: session.account.urlReferrer,
            use_showroom: session.account.summary.features?.showroom,
            use_orders: session.account.summary.features?.orders,

            company: {
              id: session.account.id,
              name: session.account.businessName,
              created_at:
                parseDate(session.account.created, true).getTime() / 1000,
              app_version: "v2",
              ...accountInfo,
            },
          }
        : {}),
    };
  } catch (e) {
    logError(e);
  }

  window.intercomSettings = shouldIdentify ? userIntercomSettings : {};
  sentCounts =
    shouldIdentify && session.account.summary
      ? countsOf(session.account.summary)
      : null;
  countsCompany = session?.account
    ? { id: session.account.id, name: session.account.businessName }
    : undefined;
  isAdminSession = session?.provider === "Admin";

  Intercom("boot", window.intercomSettings);

  // Launcher visibility = the Fin-cost gate, and the ONLY gate on Fin exposure.
  // `support === "basic"` is exactly "unpaid Tier C" (Account.AutoSupport already
  // folds in `|| IsPaying`), so the rule reads: show the launcher to everyone
  // except a first-session unpaid Tier C account.
  //
  // The `!isActivated &&` form was dropped first — it made this an 8h delay, and
  // Tier C is 92.4% day-0-only, so the delay expired for almost nobody it was aimed
  // at. `sessions > 1` replaces it with an intent signal rather than a clock: a Tier
  // C user who comes BACK has done the one thing 92% of that tier never do, and gets
  // the launcher on that second session. Same signal the old identify heuristic used.
  const isReturning = (session?.account?.summary?.sessions ?? 0) > 1;
  if (session?.account?.support === "basic" && !isReturning) {
    Intercom("update", {
      hide_default_launcher: true,
    });
  }

  if (
    session?.account?.isActivated &&
    (loginTime?.diffHours > 8 || !loginTime?.lastLoginDate)
  ) {
    Intercom("trackEvent", "new app session");
  }

  // Re-boot with the identity if the messenger is opened before one was sent.
  // Reads `window.intercomSettings` (what Intercom actually holds), NOT
  // `userIntercomSettings` (what we prepared) — the latter carries account_id for
  // every signed-in user, so the old guard was always false and this never fired.
  Intercom("onShow", () => {
    if (!window.intercomSettings.account_id) {
      Intercom("boot", userIntercomSettings);
    }
  });
};

export const isIntercomActive = () => {
  return !window.intercomSettings.account_id;
};

export const showIntercom = () => {
  if (!window.intercomSettings.account_id) {
    Intercom("boot", userIntercomSettings);
  }
  Intercom("show");
};

/**
 * Opens the messenger on a new conversation with the composer prefilled. `attributes` are written
 * to the contact first, in the same call queue, so Intercom rules (Fin's audience) can read them.
 * A custom attribute is dropped unless it was created by hand in the workspace.
 */
export const showIntercomNewMessage = (
  prefill: string,
  attributes?: Record<string, string | number>,
) => {
  if (!window.intercomSettings.account_id) {
    Intercom("boot", userIntercomSettings);
  }
  if (attributes) {
    Intercom("update", attributes);
  }
  Intercom("showNewMessage", prefill);
};

export const hideIntercomLauncher = () => {
  Intercom("update", { hide_default_launcher: true });
};

export const showIntercomLauncher = () => {
  Intercom("update", { hide_default_launcher: false });
};
